import { createSignal, type Accessor } from 'solid-js';
import type { Command } from 'Shared/posters/types';

export interface HistoryContext {
	execute:       (cmd: Command) => void
	undo:          () => void
	redo:          () => void
	clearHistory:  () => void
	markDirty:     () => void
	canUndo:       Accessor<boolean>
	canRedo:       Accessor<boolean>
	// Subscribe to history changes (execute/undo/redo/markDirty). Listeners fire
	// synchronously from those calls — they're all event-driven, so no effect is needed.
	// Returns an unsubscribe function.
	onChange:      (cb: () => void) => () => void
}

export function createHistoryState(): HistoryContext {
	const [undoStack, setUndoStack] = createSignal<Command[]>([])
	const [redoStack, setRedoStack] = createSignal<Command[]>([])

	const listeners = new Set<() => void>()
	function notifyChange() { for (const cb of listeners) cb() }
	function onChange(cb: () => void): () => void {
		listeners.add(cb)
		return () => { listeners.delete(cb) }
	}

	function execute(cmd: Command) {
		cmd.execute()
		setUndoStack(prev => {
			const last = prev[prev.length - 1]
			if (last?.mergeKey && last.mergeKey === cmd.mergeKey) {
				const merged: Command = { ...cmd, undo: last.undo }
				return [...prev.slice(0, -1).slice(-99), merged]
			}
			return [...prev.slice(-99), cmd]
		})
		setRedoStack([])
		notifyChange()
	}

	function undo() {
		const stack = undoStack()
		if (!stack.length) return
		const cmd = stack[stack.length - 1]
		cmd.undo()
		setUndoStack(prev => prev.slice(0, -1))
		setRedoStack(prev => [cmd, ...prev.slice(0, 99)])
		notifyChange()
	}

	function redo() {
		const stack = redoStack()
		if (!stack.length) return
		const cmd = stack[0]
		cmd.execute()
		setRedoStack(prev => prev.slice(1))
		setUndoStack(prev => [...prev.slice(-99), cmd])
		notifyChange()
	}

	function clearHistory() {
		setUndoStack([])
		setRedoStack([])
	}

	function markDirty() {
		notifyChange()
	}

	const canUndo: Accessor<boolean> = () => undoStack().length > 0
	const canRedo: Accessor<boolean> = () => redoStack().length > 0

	return { execute, undo, redo, clearHistory, markDirty, canUndo, canRedo, onChange }
}
